import IconChevronDown from "@shared/components/icons/ChevronDown"
import { Button } from "@shared/components/untitledui/base/buttons/button"
import {
  ColorSwatchOption,
  ColorSwatchPicker,
} from "@shared/components/untitledui/base/color-swatch-picker/color-swatch-picker"
import { Tooltip } from "@shared/components/untitledui/base/tooltip/tooltip"
import { Popover, PopoverContent } from "@shared/components/untitledui/popover"
import { cx } from "@shared/components/untitledui/util/cx"
import type { ReactNode } from "react"
import { ToggleButton as AriaToggleButton, Group, parseColor } from "react-aria-components"
import { TOOLBAR_BUTTON_ACTIVE } from "./toolbar-button-chrome"

export type SwatchItem = {
  /** The value react-aria matches selection against (`CLEAR_SWATCH` for the clear swatch). */
  color: string
  ariaLabel: string
  glyph: ReactNode
  onSelect: () => void
}

const sameColor = (a: string, b: string) =>
  parseColor(a).toString("rgba") === parseColor(b).toString("rgba")

/**
 * A Word-style split control: the main half is a real `ToggleButton` that applies (or
 * toggles off) the default value and reflects the active value as `aria-pressed`; the
 * chevron opens a `ColorSwatchPicker` for the full palette. The two halves read as one
 * control via react-aria's `Group`; the chrome (hover, pressed-in fill) lives on the
 * group so neither half double-highlights.
 *
 * The halves are stacked, not side by side: the picker is a strip across the bottom of
 * the cell, laid over the toggle. That keeps the icon centered on the same rhythm as the
 * chevron-less buttons — a side-by-side split would shove it left of center.
 */
export function ColorSplitControl({
  applyLabel,
  applyTooltip,
  pickerLabel,
  icon,
  isActive,
  isDisabled,
  activeAccent = false,
  onApply,
  items,
  selectedColor,
}: {
  applyLabel: string
  applyTooltip?: ReactNode
  pickerLabel: string
  /** Main-button icon, colored to reflect the active value. */
  icon: ReactNode
  isActive: boolean
  isDisabled: boolean
  /**
   * Whether an active value tints the glyph with the accent, like every other toolbar
   * button. Off for highlight and text color, whose icon already carries the applied
   * color; on for underline, where the color lives in a bar thin enough to miss.
   */
  activeAccent?: boolean
  onApply: () => void
  items: SwatchItem[]
  /** Controlled picker value; use `pickerValue()` to derive it from the active color. */
  selectedColor: string
}) {
  return (
    <Group
      aria-label={applyLabel}
      className={cx(
        "relative flex h-10 w-full items-stretch rounded-md text-gray-700",
        !isDisabled && "hover:bg-gray-200",
        isActive && activeAccent && TOOLBAR_BUTTON_ACTIVE,
      )}
    >
      <Tooltip title={applyTooltip ?? applyLabel} closeDelay={0}>
        <AriaToggleButton
          isSelected={isActive}
          onChange={onApply}
          isDisabled={isDisabled}
          aria-label={applyLabel}
          className={cx(
            "flex flex-1 cursor-pointer items-center justify-center rounded-md px-0 outline-brand transition",
            "focus-visible:outline-2 focus-visible:-outline-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "[&>svg]:size-4",
          )}
        >
          {icon}
        </AriaToggleButton>
      </Tooltip>
      <Popover>
        <Tooltip title={pickerLabel} closeDelay={0}>
          <Button
            isDisabled={isDisabled}
            color="tertiary"
            size="xs"
            // The `data-icon-only:` variants are what actually land: the base button's
            // own `data-icon-only:p-2` outranks a bare `p-0`.
            className="absolute inset-x-0 bottom-0 h-3 min-h-0 rounded-t-none rounded-b-md hover:bg-transparent data-icon-only:p-0"
            aria-label={pickerLabel}
            iconLeading={<IconChevronDown className="size-3" />}
          />
        </Tooltip>
        <PopoverContent placement="bottom" className="w-auto p-3">
          <ColorSwatchPicker
            aria-label={pickerLabel}
            value={selectedColor}
            onChange={(color) => {
              const key = color.toString("rgba")
              items.find((item) => sameColor(item.color, key))?.onSelect()
            }}
          >
            {items.map((item) => (
              <ColorSwatchOption key={item.ariaLabel} color={item.color} ariaLabel={item.ariaLabel}>
                {item.glyph}
              </ColorSwatchOption>
            ))}
          </ColorSwatchPicker>
        </PopoverContent>
      </Popover>
    </Group>
  )
}
